<template>
    <div
        class="sportsZone-flagBanner LivePage_Btn_Olympic2024"
        v-bind:class="{ showFlag: isShowFlags }"
        v-on:click="goToZone"
    >
        <picture class="sportsZone-flagBanner-icon">
            <source srcset="@/assets/images/sports-cup/sports_cup_olympic_icon.webp" type="image/webp">
            <img
                src="@/assets/images/sports-cup/sports_cup_olympic_icon.png"
                alt=""
                aria-hidden="true"
            >
        </picture>
        <div class="sportsZone-flagBanner-content">
            <span class="sportsZone-flagBanner-title">{{ zoneTitle }}</span>
            <template v-if="isShowFlags && match">
                <FlagIcon
                    class="sportsZone-flagBanner-country"
                    v-bind:countryName="match.homeTeam.countryName"
                />
                <span>vs</span>
                <FlagIcon
                    class="sportsZone-flagBanner-country"
                    v-bind:countryName="match.awayTeam.countryName"
                />
                <span class="sportsZone-flagBanner-time">
                    <template v-if="match.isLive">
                        {{ getTranslationBySite('live').toUpperCase() }}
                    </template>
                    <template v-else>
                        {{ showTimeDisplay }}
                    </template>
                </span>
            </template>
        </div>
        <div class="sportsZone-flagBanner-arrow">
            <img
                src="@/assets/images/multiple-sports/arrow-forward.svg"
                alt=""
                aria-hidden="true"
            >
        </div>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent } from 'vue';
    import { useRouter } from 'vue-router';
    import FlagIcon from '@/components/FlagIcon.vue';
    import useZoneBanner from '@/components/useZoneBanner';
    import { isShowFlag } from '@/libraries/flagHelper';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { i18n } from '@/libraries/i18n';
    import { useSportsZoneFlagsBannerEventsQuery } from '@/libraries/oddsApi/composables/useSportsZoneFlagsBannerEventsQuery';
    import { getEventDate } from '@/libraries/oddsApi/oddsApiFormatter';

    export default defineComponent({
        components: {
            FlagIcon,
        },
        setup() {
            const { zoneTitleKey, zoneRoute, leagueNames, sportTypeForQuery } = useZoneBanner();
            const zoneTitle = computed(() => {
                if (zoneTitleKey.value === null) {
                    return '';
                }
                return i18n.t(zoneTitleKey.value);
            });

            const router = useRouter();
            function goToZone() {
                if (zoneRoute.value !== null) {
                    router.push(zoneRoute.value);
                }
            }

            const { event: match } = useSportsZoneFlagsBannerEventsQuery({
                sportType: sportTypeForQuery,
                tournamentNames: leagueNames,
            });

            const isShowFlags = computed(() => {
                if (!match.value) {
                    return false;
                }
                return isShowFlag(match.value.homeTeam.countryName, match.value.awayTeam.countryName);
            });

            const showTimeDisplay = computed(() => (match.value
                ? `${getEventDate(match.value.showTime)} ${formatShowTime(match.value.showTime, match.value.showTimeType)}`
                : ''));

            return {
                i18n,
                getTranslationBySite,
                goToZone,
                isShowFlags,
                match,
                showTimeDisplay,
                zoneTitle,
            };
        },
    });
</script>

<style lang="scss" scoped>
.sportsZone-flagBanner {
    display: flex;
    height: 3.6rem;
    align-items: center;
    margin: 0.8rem 1rem;
    background-image:
        url("../assets/images/sports-cup/bg_sports_cup_olympic_decoMiddle.svg"),
        url("../assets/images/sports-cup/bg_sports_cup_olympic_decoRight.svg"),
        url("../assets/images/sports-cup/bg_sports_cup_olympic_deco.svg");
    background-position: center bottom 15%, right, center;
    background-repeat: no-repeat;
    background-size: 50%, contain, cover;
    border-radius: 0.5rem;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 25%);
    color: $white;
    font-weight: 700;

    &-time {
        flex: 1 1 0;
        text-align: center;
    }

    &-title {
        margin-right: 1rem;
    }

    &-icon {
        height: 100%;

        img,
        source {
            height: 100%;
            border-radius: 0.5rem;
        }
    }

    &-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-right: 1rem;
        gap: 0.5rem;
    }

    &-country {
        height: 2.625rem;
        border: 1px solid $white;
    }

    &-arrow {
        display: flex;
        align-items: center;
        margin-left: auto;
        animation-direction: alternate;
        animation-duration: 0.5s;
        animation-iteration-count: infinite;
        animation-name: move;
        animation-timing-function: linear;
    }

    @keyframes move {
        from {
            transform: translateX(0%);
        }

        to {
            transform: translateX(-50%);
        }
    }

    @media screen and (max-width: 350px) {
        .sportsZone-flagBanner-country {
            height: 2rem;
        }
    }
}
</style>
